import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wand2, ArrowRight, Sparkles, Zap, Target, Telescope,
  CheckCircle2, Plus, Minus, Mail, Loader2,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Placeholder Stripe payment link — swap for a real Stripe Payment Link when ready.
const STRIPE_LINK = "https://buy.stripe.com/test_placeholder_early_access";
const CONTACT_EMAIL = "hello@brainideasfused.app";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const USE_CASES = [
  {
    icon: Telescope,
    title: "Founder research",
    body: "Drop a week of subreddit gripes and walk away with a concept sharp enough to pitch, not a wall of screenshots.",
  },
  {
    icon: Target,
    title: "Gap analysis",
    body: "See where existing tools fall short by fusing the complaints people repeat, then aim at the exact opening.",
  },
  {
    icon: Zap,
    title: "Product validation",
    body: "Pressure-test an idea against real pain in seconds, so you build what people already ask for out loud.",
  },
];

const FAQS = [
  {
    q: "Where do the insights come from?",
    a: "You paste in Reddit post titles or short snippets — raw complaints, feature requests, anything people are saying out loud. BrainIdeasFused does the rest.",
  },
  {
    q: "Is the brief actually generated by AI?",
    a: "Yes. Claude Sonnet analyzes your inputs, finds the repeating patterns, and writes each section of the brief from scratch. No templates, no filler.",
  },
  {
    q: "Can I export the brief?",
    a: "Yes. Hit Download PDF and you get the full brief plus the prompts used to generate it, labeled and ready to share or pitch.",
  },
];

function MiniBrief() {
  const rows = [
    { k: "01 — Product Concept", v: "A one-tap tool that turns a freelancer's raw weekly notes into a polished, client-ready status report." },
    { k: "02 — Target User", v: "Solo marketers juggling 3–10 clients who dread Monday report-building." },
    { k: "03 — Value Proposition", v: "Skip the copy-paste — ship a report clients love in under five minutes." },
  ];
  return (
    <div className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl overflow-hidden shadow-2xl shadow-emerald-500/5">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border">
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-600" />
        <span className="h-2.5 w-2.5 rounded-full bg-zinc-600" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
        <span className="font-mono text-xs text-muted-foreground ml-3">brainideasfused — product brief</span>
      </div>
      <div className="p-6 sm:p-8 space-y-6">
        {rows.map((r, i) => (
          <motion.div
            key={r.k}
            variants={fadeUp}
            custom={i}
            className="border-l border-emerald-500/40 pl-4"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500 mb-1.5">{r.k}</div>
            <p className="text-sm sm:text-base leading-relaxed text-foreground/90">{r.v}</p>
          </motion.div>
        ))}
        <motion.div variants={fadeUp} custom={3}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500 mb-3">04 — 3 Key Features</div>
          <div className="grid grid-cols-3 gap-3">
            {["Auto-format weekly reports", "One-click shareable link", "Zero blank-page dread"].map((f, i) => (
              <div key={f} className="rounded-lg border border-border bg-background/50 p-3">
                <span className="font-mono text-[10px] text-emerald-500">0{i + 1}</span>
                <p className="text-xs leading-snug text-foreground/80 mt-1.5">{f}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 py-6 text-left group"
      >
        <span className="font-display text-lg sm:text-xl font-medium text-foreground group-hover:text-emerald-400 transition-colors duration-200">
          {q}
        </span>
        {open ? <Minus className="h-5 w-5 text-emerald-500 shrink-0" /> : <Plus className="h-5 w-5 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="text-base text-muted-foreground leading-relaxed pb-6 max-w-2xl"
        >
          {a}
        </motion.p>
      )}
    </div>
  );
}

export default function Landing() {
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email to join the waitlist.");
      return;
    }
    setJoining(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email });
      setJoined(true);
      toast.success(data.message || "You're on the waitlist!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? "Please enter a valid email." : detail || "Something went wrong. Try again."
      );
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen">
      <Toaster theme="dark" position="top-center" />

      {/* Nav */}
      <nav className="max-w-6xl mx-auto flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Wand2 className="h-4 w-4 text-white" />
          </div>
          <span className="font-display text-lg font-medium tracking-tight">BrainIdeasFused</span>
        </div>
        <Link
          data-testid="nav-launch-app"
          to="/app"
          className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Launch app →
        </Link>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 mb-8"
            >
              <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
              <span className="font-mono text-xs uppercase tracking-widest text-emerald-400">For indie makers</span>
            </motion.div>

            <motion.h1
              variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.03]"
            >
              Turn scattered Reddit insights into a{" "}
              <span className="text-emerald-400">fundable product brief</span> in seconds.
            </motion.h1>

            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-6 max-w-xl"
            >
              BrainIdeasFused reads the raw complaints people already post — pulls out the pain points they
              keep repeating, clusters the themes, and fuses them into a single sharp opportunity:
              concept, target user, value proposition, and three key features, each written by Claude
              and ready to pitch.
            </motion.p>

            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={3}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <Link
                data-testid="hero-launch-app"
                to="/app"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-7 py-3.5 text-base font-medium shadow-lg shadow-emerald-500/20 transition-all duration-200 hover:-translate-y-0.5"
              >
                Fuse an idea free <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                data-testid="hero-early-access"
                href={STRIPE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-border hover:border-emerald-500/50 px-7 py-3.5 text-base font-medium text-foreground transition-colors duration-200"
              >
                Get Early Access
              </a>
            </motion.div>

            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={4}
              data-testid="custom-brief-note"
              className="text-sm text-muted-foreground leading-relaxed mt-5 max-w-xl"
            >
              Serious about a custom brief or bulk access? Email{" "}
              <a
                href="mailto:support@the-mindsteinlab.com"
                className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200"
              >
                support@the-mindsteinlab.com
              </a>{" "}
              and I'll turn it around personally.
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
            className="lg:pl-4"
          >
            <MiniBrief />
          </motion.div>
        </div>
      </header>

      {/* Use cases */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20 border-t border-border">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <span className="font-mono text-xs uppercase tracking-widest text-emerald-500">What it's for</span>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-4 max-w-2xl">
            Three ways makers put BrainIdeasFused to work.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {USE_CASES.map((u, i) => (
            <motion.div
              key={u.title}
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              data-testid={`use-case-${i}`}
              className="rounded-2xl border border-border bg-card p-8 hover:border-emerald-500/40 transition-colors duration-200"
            >
              <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-6">
                <u.icon className="h-5 w-5 text-emerald-400" />
              </div>
              <h3 className="font-display text-xl font-medium mb-3">{u.title}</h3>
              <p className="text-base text-muted-foreground leading-relaxed">{u.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Waitlist */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-3xl border border-emerald-500/25 bg-gradient-to-b from-emerald-500/[0.07] to-transparent p-10 sm:p-16 text-center"
        >
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl mx-auto">
            Get on the early access list.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-4 max-w-xl mx-auto">
            Be first to unlock unlimited fusions, saved briefs, and live Reddit ingestion.
            No spam — just a note when it's ready.
          </p>

          {joined ? (
            <div data-testid="waitlist-success" className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-6 py-3.5 text-emerald-300">
              <CheckCircle2 className="h-5 w-5" />
              You're on the list — talk soon.
            </div>
          ) : (
            <form onSubmit={handleJoin} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="relative flex-1">
                <Mail className="h-4 w-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="waitlist-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-full border border-border bg-background/60 backdrop-blur pl-11 pr-4 py-3.5 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:border-emerald-500 focus-visible:outline-none transition-colors duration-200"
                />
              </div>
              <button
                data-testid="waitlist-submit-button"
                type="submit"
                disabled={joining}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-7 py-3.5 text-base font-medium transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 whitespace-nowrap"
              >
                {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Join waitlist
              </button>
            </form>
          )}
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-20 border-t border-border">
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-6"
        >
          Frequently asked.
        </motion.h2>
        <div data-testid="faq-section">
          {FAQS.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} index={i} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Wand2 className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-display font-medium tracking-tight">BrainIdeasFused</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/app" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
              Launch app
            </Link>
            <Link
              data-testid="footer-tech-spec"
              to="/tech-spec"
              className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              Security Audit
            </Link>
            <a
              data-testid="footer-contact-email"
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-emerald-400 transition-colors duration-200"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
